import { useCallback, useEffect, useMemo, useState } from 'react';

import * as FiscalPerfilAPI from '../../../../api/fiscalPerfil';

export function useFiscalProdutoModule({ dados, setDados, empresaId, setFlashMessage }) {
    const [perfisFiscais, setPerfisFiscais] = useState([]);
    const [perfilFiscalTexto, setPerfilFiscalTexto] = useState({});

    const [carregando, setCarregando] = useState(false);

    const empresaProdutoId = useMemo(() => Number(dados?.empresaId || empresaId) || null, [dados?.empresaId, empresaId]);

    const carregarPerfisFiscais = useCallback(async () => {
        if (!empresaProdutoId) {
            setPerfisFiscais([]);
            return;
        }

        setCarregando(true);

        try {
            const response = await FiscalPerfilAPI.listar({
                start: 0,
                size: 500,
                sorting: JSON.stringify([
                    {
                        id: 'codigo',
                        desc: false,
                    },
                ]),
            });

            const perfis = response?.data?.data || [];

            const permitidos = perfis
                .filter((perfil) => {
                    return Number(perfil.empresaId) === empresaProdutoId || perfil.integracaoTodasEmpresas === true;
                })
                .map((perfil) => ({
                    ...perfil,
                    id: Number(perfil.id),
                    descricao: `${perfil.descricao}`,
                    label: `${perfil.codigo}`,
                }));

            /*
             * Mantém visível o perfil atualmente vinculado, mesmo que tenha
             * sido posteriormente inativado ou retirado do compartilhamento.
             */
            const perfilAtualId = Number(dados?.fiscalPerfilId) || null;
            const perfilAtual = dados?.FiscalPerfil;

            if (perfilAtualId && perfilAtual && !permitidos.some((perfil) => perfil.id === perfilAtualId)) {
                permitidos.unshift({
                    id: perfilAtualId,
                    uuid: perfilAtual.uuid,
                    codigo: perfilAtual.codigo,
                    descricao: `${perfilAtual.codigo} - ${perfilAtual.descricao} (ATUAL)`,
                    perfilAtual: true,
                });
            }

            setPerfisFiscais(permitidos);
        } catch (error) {
            console.error(error);

            setPerfisFiscais([]);

            setFlashMessage(
                error.response?.data?.message || 'Não foi possível carregar os perfis fiscais.',
                error.response?.data?.type || 'error'
            );
        } finally {
            setCarregando(false);
        }
    }, [empresaProdutoId, dados?.fiscalPerfilId, dados?.FiscalPerfil, setFlashMessage]);

    useEffect(() => {
        setPerfilFiscalTexto(dados?.FiscalPerfil);
    }, [dados?.FiscalPerfil]);

    useEffect(() => {
        carregarPerfisFiscais();
    }, [carregarPerfisFiscais]);

    const alterarCampoFiscal = useCallback(
        (event) => {
            const { name, value } = event.target;

            let valorNormalizado = value;

            if (name === 'fiscalPerfilId') {
                valorNormalizado = value === '' ? null : Number(value);
            }

            if (name === 'origemMercadoria') {
                valorNormalizado = value === '' ? null : String(value);
            }

            setDados((anterior) => ({
                ...anterior,
                [name]: valorNormalizado,
            }));
        },
        [setDados]
    );

    const alterarPerfilFiscal = useCallback(
        (event) => {
            const texto = event.target.value;

            setPerfilFiscalTexto((anterior) => ({
                ...(anterior || {}),
                descricao: texto,
            }));

            const perfil = perfisFiscais.find((item) => item.descricao === texto);

            if (perfil) {
                setDados((anterior) => ({
                    ...anterior,
                    fiscalPerfilId: Number(perfil.id),
                    FiscalPerfil: perfil,
                }));
                return;
            }

            if (!texto.trim()) {
                setDados((anterior) => ({
                    ...anterior,
                    fiscalPerfilId: null,
                    FiscalPerfil: null,
                }));
            }
        },
        [perfisFiscais, setDados]
    );

    const perfilSelecionado = useMemo(() => {
        const fiscalPerfilId = Number(dados?.fiscalPerfilId) || null;

        return perfisFiscais.find((perfil) => Number(perfil.id) === fiscalPerfilId) || null;
    }, [dados?.fiscalPerfilId, perfisFiscais]);

    return {
        state: {
            perfisFiscais,
            perfilFiscalTexto,
            perfilSelecionado,
            carregando,
        },
        actions: {
            alterarCampoFiscal,
            alterarPerfilFiscal,
            carregarPerfisFiscais,
        },
    };
}
